import {PopupContainer} from "@FRAMEWORK/ui/desktop/popup";
import {TableContainer} from "@FRAMEWORK/ui/desktop/table";
import {TextButton} from "@FRAMEWORK/ui/desktop/button";
import {EditorBox} from "@FRAMEWORK/ui/desktop/editor";
import * as style from "@COMPONENTS/style/productlist/compareevent";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import {OverlayState} from "@/hooks/common/useOverlay";
import {EventCompareType} from "@QUERIES/prdt/event/useEventCompare";
import {useMemo} from "react";
import {format} from "@yb/utils";
import {departureStart, getTrafficIconUrl, returnArrival} from "@COMPONENTS/common/eventUtil";
import {useRouter} from "next/router";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";

const CompareEventPopupComponent = (props: OverlayState & EventCompareType) => {
	const router = useRouter();
	const {compareList, compareScheduleList, compareSelTripList, ...overlayProps} = props;

	const Colgroup = useMemo(() => {
		if (compareList && compareList.length === 2) {
			return (
				<colgroup>
					<col style={{width: "8%"}} />
					<col style={{width: "8%"}} />
					<col style={{width: "42%"}} />
					<col style={{width: "42%"}} />
				</colgroup>
			);
		} else {
			return (
				<colgroup>
					<col style={{width: "9%"}} />
					<col style={{width: "7%"}} />
					<col style={{width: "28%"}} />
					<col style={{width: "28%"}} />
					<col style={{width: "28%"}} />
				</colgroup>
			);
		}
	}, [compareList]);

	return (
		<PopupContainer {...overlayProps} title="비교하기" size="large" paddingTop={false}>
			<style.TableWrap>
				<TableContainer lineTop={false} types="vertical">
					<caption>행사 비교</caption>
					{Colgroup}
					<tbody>
						<tr>
							<th scope="row" colSpan={2}>
								상품번호
							</th>
							{compareList.map((item, index) => (
								<td key={index}>{item.evCd}</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								상품명
							</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<style.ProductExplain>{item.evNm}</style.ProductExplain>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" rowSpan={3}>
								상품가
							</th>
							<th scope="row">성인</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<style.ProductPrice>
										<em>{format.koKRFormatNumber(item.adtPrice)}</em> 원
									</style.ProductPrice>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row">아동</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<style.ProductPrice>
										<em>{format.koKRFormatNumber(item.chdPrice)}</em> 원
									</style.ProductPrice>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row">유아</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<style.ProductPrice>
										<em>{format.koKRFormatNumber(item.infPrice)}</em> 원
									</style.ProductPrice>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								여행 기간
							</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<div>{item.evNightNm}</div>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" rowSpan={3} colSpan={2}>
								일정
							</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<Flex gap="10" align="center">
										<img
											src={getTrafficIconUrl({trTp: item.trTp, trCompanySnm: item.outAirCd})}
											alt={item.outAirCompany}
										/>
										{item.outAirCompany}
									</Flex>
								</td>
							))}
						</tr>
						<tr>
							{compareList.map((item, index) => {
								const goodsAreaOne = item.evCd.substring(0, 1);
								return (
									<td key={index}>
										<Flex gap="5" align="center" wrap="wrap">
											<style.AirplaneDepart>{(goodsAreaOne !== "K" && "한국") || ""}출발</style.AirplaneDepart>
											<style.AirplaneNumber>{item.outFlightNm}</style.AirplaneNumber>
											<style.AirplaneDay>{departureStart(item)}</style.AirplaneDay>
										</Flex>
									</td>
								);
							})}
						</tr>
						<tr>
							{compareList.map((item, index) => {
								const goodsAreaOne = item.evCd.substring(0, 1);
								return (
									<td key={index}>
										<Flex gap="5" align="center" wrap="wrap">
											<style.AirplaneDepart>{(goodsAreaOne !== "K" && "한국") || ""}도착</style.AirplaneDepart>
											<style.AirplaneNumber>{item.inFlightNm}</style.AirplaneNumber>
											<style.AirplaneDay>
												{returnArrival({evArriveDt: item.inArriveDt, inArrvTm: item.inArrvTm})}
											</style.AirplaneDay>
										</Flex>
									</td>
								);
							})}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								방문 도시
							</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<div>{item?.goodsTourInfo}</div>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								숙박
							</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<div>{item?.accomNm}</div>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								포함 사항
							</th>
							{compareScheduleList.map((item, index) => (
								<td key={index}>
									<EditorBox dangerouslySetInnerHTML={{__html: item?.inclInfo?.replace(/\r\n/g, "<br/>") || ""}} />
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								불포함 사항
							</th>
							{compareScheduleList.map((item, index) => (
								<td key={index}>
									<EditorBox dangerouslySetInnerHTML={{__html: item?.notinclInfo?.replace(/\r\n/g, "<br/>")}} />
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								참고/전달사항
							</th>
							{compareScheduleList.map((item, index) => (
								<td key={index}>
									<EditorBox dangerouslySetInnerHTML={{__html: item?.refeInfo?.replace(/\r\n/g, "<br/>")}} />
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								선택관광
							</th>
							{compareSelTripList?.map((compareSelTrip, tripIndex) => (
								<td key={tripIndex}>
									<style.TourList>
										{compareSelTrip?.map((item, index) => (
											<style.TourListItem key={index}>
												<style.TourName>{item.title}</style.TourName>
												<style.TourPrice>{item.cost}</style.TourPrice>
											</style.TourListItem>
										))}
									</style.TourList>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}>
								쇼핑
							</th>
							{compareList.map((item, index) => (
								<td key={index}>
									<div>{item.shopCnt}회</div>
								</td>
							))}
						</tr>
						<tr>
							<th scope="row" colSpan={2}></th>
							{compareList.map((item, index) => (
								<td key={index}>
									<style.BtnBlock>
										<TextButton
											text="상품 상세보기"
											size="default"
											types="basic"
											onClick={() => {
												// 링크 확인 필요
												router.push(item.webLinkUrl.replace(".yb", "") || "#");
												overlayProps.close();
											}}
										/>
									</style.BtnBlock>
								</td>
							))}
						</tr>
					</tbody>
				</TableContainer>
			</style.TableWrap>
		</PopupContainer>
	);
};

const CompareEventPopup = (props: OverlayState & EventCompareType) => (
	<ErrorBoundary logMsg="CompareEventPopup Error">
		<CompareEventPopupComponent {...props} />
	</ErrorBoundary>
);

export default CompareEventPopup;
